import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaWrench, FaTools, FaBroom, FaHardHat, FaPaintRoller } from 'react-icons/fa';
import { theme } from '../../styles/theme';

// Page Header
const PageHeader = styled.div`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(184, 115, 51, 0.1)),
              linear-gradient(to right, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.7)),
              url('/images/safirst-bg.jpg') no-repeat center center;
  background-size: cover;
  min-height: 60vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 120px 24px 80px;
  text-align: center;
`;

const HeaderContent = styled.div`
  max-width: 900px;
`;

const WelcomeBadge = styled(motion.div)`
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 8px 24px;
  border-radius: ${theme.borderRadius.pill};
  font-size: 0.9rem;
  font-weight: ${theme.typography.fontWeight.semiBold};
  display: inline-block;
  margin-bottom: 24px;
`;

const MainHeading = styled.h1`
  font-size: 3.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 24px;
  line-height: 1.2;
  
  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SubHeading = styled.p`
  font-size: 1.3rem;
  color: ${theme.colors.text.primary};
  margin-bottom: 40px;
  line-height: 1.6;
  
  @media screen and (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const CTAButton = styled(motion.a)`
  display: inline-block;
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 18px 48px;
  border-radius: ${theme.borderRadius.pill};
  font-size: 1.2rem;
  font-weight: ${theme.typography.fontWeight.semiBold};
  text-decoration: none;
  box-shadow: ${theme.shadows.large};
  
  &:hover {
    background: ${theme.colors.tertiary};
    color: ${theme.colors.text.primary};
  }
  
  @media screen and (max-width: 480px) {
    padding: 16px 32px;
    font-size: 1.1rem;
  }
`;

// Section Container
const SectionContainer = styled.div`
  padding: 100px 0;
  background: ${theme.colors.primary};
  
  @media screen and (max-width: 768px) {
    padding: 70px 0;
  }
`;

const SectionWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 24px;
  text-align: center;
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SectionSubheading = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.text.primary};
  margin-bottom: 64px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

// Value Proposition
const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
  margin-bottom: 64px;
`;

const ValueCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: ${theme.borderRadius.large};
  padding: 32px;
  text-align: center;
  
  &:hover {
    border-color: rgba(212, 175, 55, 0.2);
    box-shadow: ${theme.shadows.large};
  }
`;

const ValueIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.secondary};
  margin-bottom: 20px;
`;

const ValueTitle = styled.h3`
  font-size: 1.3rem;
  color: ${theme.colors.secondary};
  margin-bottom: 12px;
`;

const ValueDescription = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  line-height: 1.5;
`;

// Examples Section
const ExamplesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 64px;
`;

const ExampleCard = styled(motion.div)`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.gold};
  border-radius: ${theme.borderRadius.large};
  padding: 24px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.medium};
  }
`;

const ExampleIcon = styled.div`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 16px;
`;

const ExampleTitle = styled.h4`
  font-size: 1.1rem;
  color: ${theme.colors.secondary};
  margin-bottom: 8px;
`;

const ExampleBadge = styled.span`
  background: rgba(212, 175, 55, 0.2);
  color: ${theme.colors.secondary};
  padding: 4px 12px;
  border-radius: ${theme.borderRadius.pill};
  font-size: 0.8rem;
`;

// Pricing Section
const PricingCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(184, 115, 51, 0.05));
  border: 2px solid ${theme.colors.secondary};
  border-radius: ${theme.borderRadius.large};
  padding: 48px;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
`;

const PriceTag = styled.div`
  font-size: 3rem;
  color: ${theme.colors.secondary};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: 16px;
`;

const PriceNote = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.text.primary};
  margin-bottom: 24px;
`;

// Trust Signals
const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-top: 64px;
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: ${theme.borderRadius.medium};
`;

const TrustIcon = styled(FaCheckCircle)`
  color: ${theme.colors.secondary};
  font-size: 1.2rem;
`;

const TrustText = styled.span`
  color: ${theme.colors.text.primary};
  font-size: 1rem;
`;

// Form Section
const FormSection = styled.div`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(184, 115, 51, 0.1));
  border: 1px solid ${theme.colors.border.gold};
  border-radius: ${theme.borderRadius.large};
  padding: 48px;
  max-width: 600px;
  margin: 0 auto;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  color: ${theme.colors.secondary};
  font-size: 0.9rem;
  margin-bottom: 8px;
  font-weight: ${theme.typography.fontWeight.medium};
`;

const FormInput = styled.input`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.gold};
  border-radius: ${theme.borderRadius.medium};
  padding: 14px 16px;
  color: ${theme.colors.text.primary};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.secondary};
    box-shadow: ${theme.shadows.focus};
  }
  
  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const FormSelect = styled.select`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.gold};
  border-radius: ${theme.borderRadius.medium};
  padding: 14px 16px;
  color: ${theme.colors.text.primary};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.secondary};
    box-shadow: ${theme.shadows.focus};
  }
`;

const SubmitButton = styled(motion.button)`
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 16px 32px;
  border-radius: ${theme.borderRadius.pill};
  font-size: 1.1rem;
  font-weight: ${theme.typography.fontWeight.semiBold};
  border: none;
  cursor: pointer;
  margin-top: 16px;
  box-shadow: ${theme.shadows.medium};
  
  &:hover {
    background: ${theme.colors.tertiary};
    color: ${theme.colors.text.primary};
  }
`;

const SA1stWorkersLanding = () => {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    service: '',
    phone: '',
    email: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Construct WhatsApp message
    const message = `*New Website Quote Request from SA1stWorkers*

*Name:* ${formData.name}
*Business Name:* ${formData.businessName}
*Service:* ${formData.service}
*Phone:* ${formData.phone}
*Email:* ${formData.email}

This is a quote request from the SA1stWorkers landing page.`;

    // WhatsApp number (South Africa format: +27 followed by number without leading 0)
    const whatsappNumber = '27740217708'; // 0740217708

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);

    // Redirect to WhatsApp
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <PageHeader>
        <HeaderContent>
          <WelcomeBadge
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Welcome, SA1stWorkers Professional!
          </WelcomeBadge>
          <MainHeading
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Need a Website for Your Business?
          </MainHeading>
          <SubHeading
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            You're a skilled professional—your website should work just as hard as you do.
            We help electricians, plumbers, cleaners, mechanics, builders, and contractors get online quickly.
          </SubHeading>
          <CTAButton
            href="#quote-form"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get My Free Quote
          </CTAButton>
        </HeaderContent>
      </PageHeader>

      <SectionContainer>
        <SectionWrapper>
          <SectionHeading>As a member of SA1stWorkers</SectionHeading>
          <SectionSubheading>
            You can request a free, no-obligation website quotation from Afrotech Group.
            We understand your business and build websites that work for tradespeople.
          </SectionSubheading>

          <SectionHeading>Your website can help you</SectionHeading>
          <ValueGrid>
            <ValueCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <ValueIcon><FaCheckCircle /></ValueIcon>
              <ValueTitle>Get More Customers</ValueTitle>
              <ValueDescription>Be found by people searching for your services online</ValueDescription>
            </ValueCard>
            <ValueCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ValueIcon><FaCheckCircle /></ValueIcon>
              <ValueTitle>Accept Quotes 24/7</ValueTitle>
              <ValueDescription>Let customers request quotes anytime, even when you're working</ValueDescription>
            </ValueCard>
            <ValueCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <ValueIcon><FaCheckCircle /></ValueIcon>
              <ValueTitle>Build Trust</ValueTitle>
              <ValueDescription>Showcase your work and reviews to new clients</ValueDescription>
            </ValueCard>
            <ValueCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <ValueIcon><FaCheckCircle /></ValueIcon>
              <ValueTitle>Showcase Your Work</ValueTitle>
              <ValueDescription>Display photos of completed projects to impress potential clients</ValueDescription>
            </ValueCard>
            <ValueCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <ValueIcon><FaCheckCircle /></ValueIcon>
              <ValueTitle>WhatsApp Enquiries</ValueTitle>
              <ValueDescription>Let customers contact you directly via WhatsApp</ValueDescription>
            </ValueCard>
          </ValueGrid>

          <SectionHeading>Websites We Build</SectionHeading>
          <SectionSubheading>
            Professional websites tailored for your trade
          </SectionSubheading>
          <ExamplesGrid>
            <ExampleCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <ExampleIcon><FaWrench /></ExampleIcon>
              <ExampleTitle>Electrician Website</ExampleTitle>
              <ExampleBadge>Example</ExampleBadge>
            </ExampleCard>
            <ExampleCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
            >
              <ExampleIcon><FaTools /></ExampleIcon>
              <ExampleTitle>Plumbing Website</ExampleTitle>
              <ExampleBadge>Example</ExampleBadge>
            </ExampleCard>
            <ExampleCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
            >
              <ExampleIcon><FaBroom /></ExampleIcon>
              <ExampleTitle>Cleaning Company</ExampleTitle>
              <ExampleBadge>Example</ExampleBadge>
            </ExampleCard>
            <ExampleCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
            >
              <ExampleIcon><FaHardHat /></ExampleIcon>
              <ExampleTitle>Construction</ExampleTitle>
              <ExampleBadge>Example</ExampleBadge>
            </ExampleCard>
            <ExampleCard
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
            >
              <ExampleIcon><FaPaintRoller /></ExampleIcon>
              <ExampleTitle>Painter</ExampleTitle>
              <ExampleBadge>Example</ExampleBadge>
            </ExampleCard>
          </ExamplesGrid>

          <PricingCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <PriceTag>Websites starting from R2,500</PriceTag>
            <PriceNote>Flexible payment options available</PriceNote>
            <CTAButton
              href="#quote-form"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Your Free Quote
            </CTAButton>
          </PricingCard>

          <TrustGrid>
            <TrustItem>
              <TrustIcon />
              <TrustText>South African Company</TrustText>
            </TrustItem>
            <TrustItem>
              <TrustIcon />
              <TrustText>Custom-Built Websites</TrustText>
            </TrustItem>
            <TrustItem>
              <TrustIcon />
              <TrustText>Mobile-Friendly</TrustText>
            </TrustItem>
            <TrustItem>
              <TrustIcon />
              <TrustText>Google-Ready</TrustText>
            </TrustItem>
            <TrustItem>
              <TrustIcon />
              <TrustText>Ongoing Support</TrustText>
            </TrustItem>
            <TrustItem>
              <TrustIcon />
              <TrustText>Secure Hosting</TrustText>
            </TrustItem>
          </TrustGrid>
        </SectionWrapper>
      </SectionContainer>

      <SectionContainer style={{ background: 'rgba(0, 0, 0, 0.3)' }}>
        <SectionWrapper>
          <SectionHeading>Get Your Free Website Quote</SectionHeading>
          <SectionSubheading>
            Fill in the form below and we'll get back to you within 24 hours
          </SectionSubheading>

          <FormSection id="quote-form">
            <FormGrid>
              <FormGroup>
                <FormLabel>Your Name</FormLabel>
                <FormInput
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Business Name</FormLabel>
                <FormInput
                  type="text"
                  name="businessName"
                  placeholder="John's Electrical Services"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Service You Provide</FormLabel>
                <FormSelect
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select your service</option>
                  <option value="electrician">Electrician</option>
                  <option value="plumber">Plumber</option>
                  <option value="cleaner">Cleaner</option>
                  <option value="mechanic">Mechanic</option>
                  <option value="builder">Builder</option>
                  <option value="painter">Painter</option>
                  <option value="carpenter">Carpenter</option>
                  <option value="other">Other</option>
                </FormSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel>Phone Number</FormLabel>
                <FormInput
                  type="tel"
                  name="phone"
                  placeholder="+27 12 345 6789"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Email Address</FormLabel>
                <FormInput
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <SubmitButton
                type="submit"
                onClick={handleSubmit}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Request Free Quote via WhatsApp
              </SubmitButton>
            </FormGrid>
          </FormSection>
        </SectionWrapper>
      </SectionContainer>
    </>
  );
};

export default SA1stWorkersLanding;
